/**
 * Gate Pass — enums and display labels (mirrors /api/v1/gate-pass, stock-ledger-service).
 *
 * Values are the backend's strings verbatim. Never re-spell an enum on the client:
 * a label belongs in the *_LABELS map, not in the value.
 */

export const GATE_PASS_DIRECTION_LABELS = {
  OUTWARD: 'Outward',
  INWARD: 'Inward',
};

/** The segmented control on the form. `hint` is the sub-label under each option. */
export const RETURNABLE_OPTIONS = [
  { value: 'RETURNABLE', label: 'Returnable', hint: 'Item will come back' },
  { value: 'NON_RETURNABLE', label: 'Non-Returnable', hint: 'Item will not come back' },
];

export const GATE_PASS_PURPOSES = [
  'REPAIR',
  'CALIBRATION',
  'CAMP',
  'VENDOR_RETURN',
  'SCRAP_DISPOSAL',
  'DONATION',
  'DEMO',
  'OTHER',
];

export const GATE_PASS_PURPOSE_LABELS = {
  REPAIR: 'Repair',
  CALIBRATION: 'Calibration',
  CAMP: 'Health Camp',
  VENDOR_RETURN: 'Vendor Return',
  SCRAP_DISPOSAL: 'Scrap / Disposal',
  DONATION: 'Donation',
  DEMO: 'Demo / Trial',
  OTHER: 'Other',
};

/**
 * Which way the Returnable toggle should start when a purpose is picked.
 *
 * A suggestion, never a lock — the user can always flip it. Picking the wrong side
 * of that toggle is the one mistake on this form that silently changes whether stock
 * moves, so the form should not open on a coin-flip.
 */
export const PURPOSE_DEFAULT_RETURNABLE = {
  REPAIR: 'RETURNABLE',
  CALIBRATION: 'RETURNABLE',
  CAMP: 'RETURNABLE',
  DEMO: 'RETURNABLE',
  VENDOR_RETURN: 'NON_RETURNABLE',
  SCRAP_DISPOSAL: 'NON_RETURNABLE',
  DONATION: 'NON_RETURNABLE',
};

/**
 * The free-text behind purpose=OTHER (`purpose_remarks`).
 *
 * "Other" on its own is not a gate register entry — the guard at the barrier and
 * the auditor six months later both read this line and nothing else. So the field
 * is required whenever OTHER is picked, and it says what a good answer looks like
 * instead of leaving an empty box labelled "remarks".
 */
export const PURPOSE_REMARKS_LABEL = 'Specify purpose *';

export const PURPOSE_REMARKS_PLACEHOLDER = 'e.g. Samples sent to outside lab for testing';

// export const PURPOSE_REMARKS_HELPER =
  // 'Write why the material is leaving the hospital, in one line — this is what the '
  // + 'gate and the audit trail read. Examples: "Samples sent to outside lab", '
  // + '"Equipment loaned to sister facility", "Instrument sent for exhibition".';

/**
 * What one gate pass line IS — not a backend enum, a client-side shape.
 *
 * The server stores one nullable `item_id` and decides nothing from it. The two
 * values here are simply which half of GatePassItem a row fills in:
 *
 *   STORE  item_id + grn_item_id — a real balance in the selected store.
 *   OTHER  item_description + serial_number — an off-master asset. The ward AC
 *          that has gone out for repair was never a stock row, so there is no
 *          item and no batch to name; the serial number is what identifies the
 *          physical piece that has to come back.
 *
 * Derived back from the saved document as `item_id ? STORE : OTHER`, so nothing
 * extra is persisted and an older pass still opens in the right shape.
 */
export const GATE_PASS_LINE_TYPES = {
  STORE: 'STORE',
  OTHER: 'OTHER',
};

export const GATE_PASS_LINE_TYPE_OPTIONS = [
  { value: GATE_PASS_LINE_TYPES.STORE, label: 'Store item' },
  { value: GATE_PASS_LINE_TYPES.OTHER, label: 'Other' },
];

/**
 * `source_document_type` values accepted by GatePassCreate. STOCK_TRANSFER is a
 * fourth one the service knows about; it is absent here because no purpose on
 * this form leads to it.
 */
export const GATE_PASS_SOURCE_TYPES = {
  VENDOR_RETURN: 'VENDOR_RETURN',
  GOODS_DISCARD: 'GOODS_DISCARD',
};

/**
 * Which purpose puts the pass on the back of an existing document.
 *
 * A gate pass posts nothing — GatePassService is explicit that it never touches
 * stock, in either direction. So the source document is not a posting guard, it
 * is the answer to "which material is this": the Vendor Return or the Goods
 * Discard already decided the items and the quantities, and re-typing them is
 * how the challan ends up disagreeing with the document it accompanies.
 */
export const GATE_PASS_SOURCE_BY_PURPOSE = {
  VENDOR_RETURN: GATE_PASS_SOURCE_TYPES.VENDOR_RETURN,
  SCRAP_DISPOSAL: GATE_PASS_SOURCE_TYPES.GOODS_DISCARD,
};

export const GATE_PASS_SOURCE_LABELS = {
  VENDOR_RETURN: 'Vendor Return',
  GOODS_DISCARD: 'Goods Discard',
};

export const GATE_PASS_PARTY_TYPES = ['VENDOR', 'STAFF', 'EXTERNAL_ORG'];

export const GATE_PASS_PARTY_TYPE_LABELS = {
  VENDOR: 'Vendor',
  // PATIENT: 'Patient',
  STAFF: 'Staff',
  EXTERNAL_ORG: 'External Organisation',
};

export const GATE_PASS_STATUS = {
  DRAFT: 'DRAFT',
  /** Reserved — the HOD stage is deferred, so nothing reaches this today. */
  PENDING_DEPARTMENT: 'PENDING_DEPARTMENT',
  PENDING_STORE: 'PENDING_STORE',
  APPROVED: 'APPROVED',
  GATE_OUT: 'GATE_OUT',
  GATE_IN: 'GATE_IN',
  PARTIALLY_RETURNED: 'PARTIALLY_RETURNED',
  RETURNED: 'RETURNED',
  CLOSED: 'CLOSED',
  CLOSED_NOT_RETURNED: 'CLOSED_NOT_RETURNED',
  REJECTED: 'REJECTED',
  CANCELLED: 'CANCELLED',
};

export const GATE_PASS_STATUS_LABELS = {
  DRAFT: 'Draft',
  PENDING_DEPARTMENT: 'Pending HOD',
  PENDING_STORE: 'Pending Approval',
  APPROVED: 'Ready to Leave',
  GATE_OUT: 'Gate Out',
  GATE_IN: 'Gate In',
  PARTIALLY_RETURNED: 'Partly Returned',
  RETURNED: 'Returned',
  CLOSED: 'Closed',
  CLOSED_NOT_RETURNED: 'Not Returned',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
};

/** Approval stages. Phase 1 reaches STORE and SECURITY only — see GATE_PASS_BACKEND.md §3. */
export const GATE_PASS_STAGES = {
  DEPARTMENT: 'DEPARTMENT',
  STORE: 'STORE',
  SECURITY: 'SECURITY',
};

export const GATE_PASS_STAGE_LABELS = {
  DEPARTMENT: 'Needs HOD approval',
  STORE: 'Needs approval',
  SECURITY: 'Ready to leave',
};

export const GATE_PASS_STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All Status' },
  ...Object.keys(GATE_PASS_STATUS_LABELS).map((value) => ({
    value,
    label: GATE_PASS_STATUS_LABELS[value],
  })),
];

